/* --------------------------------------------------------------
 * Testimonials — "Read more".
 *
 * Quotes are clamped to a fixed number of lines in CSS. This file
 * measures each card, reveals the Read more button only on the cards
 * that actually overflow, and opens the full testimonial in the
 * per-section modal.
 *
 * Every `[data-testimonials-root]` initialises itself, so multiple
 * instances on a page (and the theme editor's section reloads) are safe.
 * ------------------------------------------------------------ */
(function () {
  'use strict';

  // The section renders one <script src> per instance; only define once.
  if (window.XenonTestimonials) {
    window.XenonTestimonials.initAll();
    return;
  }

  var OPEN_CLASS = 'testimonial-modal-open';
  var instances = [];
  var openInstance = null;

  function setText(el, value) {
    if (!el) return;
    el.textContent = value || '';
    el.hidden = !value;
  }

  // Merchants author testimonials as multi-line text, so the popup rebuilds the
  // paragraph structure they typed: a blank line starts a new <p>, single line
  // breaks stay inside one (white-space: pre-line renders them). Built with
  // textContent per paragraph rather than innerHTML so the copy is never parsed
  // as markup.
  function setQuote(el, value) {
    if (!el) return;
    el.textContent = '';
    var text = (value || '').trim();
    el.hidden = !text;
    if (!text) return;
    text.split(/\n\s*\n/).forEach(function (para) {
      var trimmed = para.trim();
      if (!trimmed) return;
      var p = document.createElement('p');
      p.className = 'testimonials-modal__para';
      p.textContent = trimmed;
      el.appendChild(p);
    });
  }

  function Testimonials(root) {
    var modal = root.querySelector('[data-testimonial-modal]');
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-testimonial-card]'));
    if (!modal || !cards.length) return null;

    var closeBtn = modal.querySelector('[data-testimonial-close-btn]');
    var quoteEl = modal.querySelector('[data-testimonial-modal-quote]');
    var nameEl = modal.querySelector('[data-testimonial-modal-name]');
    var metaEl = modal.querySelector('[data-testimonial-modal-meta]');
    var figureEl = modal.querySelector('[data-testimonial-modal-figure]');
    var imageEl = modal.querySelector('[data-testimonial-modal-image]');

    var isOpen = false;
    var lastFocused = null;
    var resizeTimer = null;
    var api;

    /* ---------- Overflow measurement ---------- */

    function syncCard(card) {
      var text = card.querySelector('[data-testimonial-text]');
      var btn = card.querySelector('[data-testimonial-more]');
      if (!text || !btn) return;
      // 1px of slack absorbs sub-pixel line-height rounding.
      var overflows = text.scrollHeight - text.clientHeight > 1;
      btn.hidden = !overflows;
      card.classList.toggle('is-clamped', overflows);
    }

    function measure() {
      cards.forEach(syncCard);
    }

    function onResize() {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(measure, 150);
    }

    // The carousel sets the slide widths after this script runs, so watch the
    // quotes themselves rather than relying on the initial pass alone.
    function observe() {
      if (typeof ResizeObserver !== 'function') return null;
      var ro = new ResizeObserver(function () {
        measure();
      });
      cards.forEach(function (card) {
        var text = card.querySelector('[data-testimonial-text]');
        if (text) ro.observe(text);
      });
      return ro;
    }

    /* ---------- Modal ---------- */

    function fill(card) {
      var text = card.querySelector('[data-testimonial-text]');
      var name = card.querySelector('[data-testimonial-name]');
      var meta = card.querySelector('[data-testimonial-meta]');
      var src = card.getAttribute('data-portrait-src');

      setQuote(quoteEl, text ? text.textContent : '');
      setText(nameEl, name ? name.textContent.trim() : '');
      setText(metaEl, meta ? meta.textContent.trim() : '');

      if (figureEl && imageEl) {
        if (src) {
          imageEl.src = src;
          imageEl.alt = card.getAttribute('data-portrait-alt') || '';
          figureEl.hidden = false;
        } else {
          imageEl.removeAttribute('src');
          figureEl.hidden = true;
        }
      }
    }

    function open(card) {
      if (isOpen) return;
      lastFocused = document.activeElement;
      isOpen = true;
      openInstance = api;
      fill(card);
      modal.setAttribute('data-open', 'true');
      modal.removeAttribute('aria-hidden');
      modal.removeAttribute('inert');
      document.documentElement.classList.add(OPEN_CLASS);
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      openInstance = null;
      modal.setAttribute('data-open', 'false');
      modal.setAttribute('aria-hidden', 'true');
      modal.setAttribute('inert', '');
      document.documentElement.classList.remove(OPEN_CLASS);
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
      lastFocused = null;
    }

    function trapFocus(event) {
      var focusable = Array.prototype.slice
        .call(modal.querySelectorAll('button:not([disabled])'))
        .filter(function (el) {
          return el.offsetParent !== null;
        });
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function handleKey(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        close();
      } else if (event.key === 'Tab') {
        trapFocus(event);
      }
    }

    /* ---------- Wiring ---------- */

    cards.forEach(function (card) {
      var btn = card.querySelector('[data-testimonial-more]');
      if (!btn) return;
      btn.addEventListener('click', function () {
        open(card);
      });
    });

    Array.prototype.forEach.call(modal.querySelectorAll('[data-testimonial-close]'), function (el) {
      el.addEventListener('click', close);
    });

    window.addEventListener('resize', onResize);
    var observer = observe();

    measure();
    // Web fonts change the line box height, so re-measure once they land.
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(measure);
    }

    api = {
      root: root,
      close: close,
      handleKey: handleKey,
      measure: measure,
      isOpen: function () {
        return isOpen;
      },
      destroy: function () {
        window.removeEventListener('resize', onResize);
        if (observer) observer.disconnect();
      }
    };

    return api;
  }

  function init(root) {
    if (!root || root.getAttribute('data-testimonials-init') === 'true') return;
    var instance = Testimonials(root);
    if (!instance) return;
    root.setAttribute('data-testimonials-init', 'true');
    instances.push(instance);
  }

  function initAll(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    if (context.matches && context.matches('[data-testimonials-root]')) init(context);
    Array.prototype.forEach.call(context.querySelectorAll('[data-testimonials-root]'), init);
  }

  document.addEventListener('keydown', function (event) {
    if (openInstance) openInstance.handleKey(event);
  });

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    instances = instances.filter(function (instance) {
      if (!event.target.contains(instance.root)) return true;
      if (instance.isOpen()) instance.close();
      instance.destroy();
      return false;
    });
  });

  window.XenonTestimonials = { init: init, initAll: initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }
})();
